import axios from "axios";
import { showGlobalToast } from "../components/shared/toastGlobal";
import { navigateGlobal } from "../components/shared/navigationGlobal";
import { clearAuthSnapshot } from "./authutils";
import { normalizeLang } from "./translation";

const ACCESS_DENIED_PATH = "/dashboard/access-denied";

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "https://api.asseteye.ai",
  withCredentials: true,
});

// No-auth instance for public (login-free) endpoints, e.g. the shared damage
// viewer. Attaches the language param but never a token, and skips the global
// 401 → /login redirect so a logged-out visitor stays on the public page.
export const publicApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "https://api.asseteye.ai",
});

publicApi.interceptors.request.use((config) => {
  // An explicit `language` param (e.g. the public viewer's switcher) wins over the
  // stored app language, so a visitor can switch without changing global state.
  const lang = normalizeLang(config.params?.language || localStorage.getItem("i18nextLng"));
  config.params = { ...(config.params || {}), language: lang };
  return config;
});

export const API_BASE_PREFIX = "/dronetjek";

const CSRF_HEADER_VALUE = "asseteye-fe";
const MUTATING_METHODS = ["post", "put", "patch", "delete"];

api.interceptors.request.use(
  (config) => {
    const lang = normalizeLang(localStorage.getItem("i18nextLng"));
    config.params = {
      ...(config.params || {}),
      language: lang,
    };

    if (MUTATING_METHODS.includes((config.method || "").toLowerCase())) {
      config.headers = config.headers || {};
      if (!config.headers["X-CSRFToken"]) {
        config.headers["X-CSRFToken"] = CSRF_HEADER_VALUE;
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

const AUTH_PATHS = ["/login/", "/refresh/", "/logout/"];
const isAuthPath = (url = "") => {
  const path = url.split("?")[0];
  return AUTH_PATHS.some((p) => path.endsWith(p));
};

let isRefreshing = false;
let refreshPromise = null;

const doRefresh = () => {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshPromise = api
      .post(`${API_BASE_PREFIX}/refresh/`, null, { _silentAuth: true })
      .then((res) => res)
      .finally(() => {
        isRefreshing = false;
      });
  }
  return refreshPromise;
};

export const ensureFreshSession = () => doRefresh();

const hardLogout = () => {
  try {
    api.post(`${API_BASE_PREFIX}/logout/`, null, { _silentAuth: true }).catch(() => {});
  } catch (_) {}
  localStorage.removeItem("selectedShape");
  clearAuthSnapshot();
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const backend = error?.response?.data;
    const originalConfig = error?.config || {};
    const url = originalConfig.url || "";

    const dynamicMessage =
      backend?.message ||
      backend?.error ||
      backend?.errors?.[Object.keys(backend?.errors || {})[0]] ||
      "Something went wrong!";

    if (status === 409) {
      showGlobalToast(dynamicMessage, "info");
      return Promise.reject(error);
    }

    if (status === 403) {
      if (!isAuthPath(url)) {
        const isPageLoad =
          !originalConfig._userAction && (originalConfig.method || "get").toLowerCase() === "get";
        const alreadyOnDeniedPage =
          typeof window !== "undefined" && window.location.pathname === ACCESS_DENIED_PATH;

        // A forbidden page-load has no data to render, so replace the broken
        // view with the access-denied page; a forbidden action keeps the user
        // where they are and only surfaces a toast.
        if (isPageLoad && !alreadyOnDeniedPage) {
          navigateGlobal(ACCESS_DENIED_PATH, { replace: true });
        } else {
          showGlobalToast(
            dynamicMessage || "You do not have permission to perform this action.",
            "error",
          );
        }
      }
      return Promise.reject(error);
    }

    if (status === 401) {
      const silent = originalConfig._silentAuth;
      const endSession = () => (silent ? clearAuthSnapshot() : hardLogout());

      if (isAuthPath(url)) {
        endSession();
        return Promise.reject(error);
      }
      if (originalConfig._retry) {
        endSession();
        return Promise.reject(error);
      }
      originalConfig._retry = true;
      try {
        await doRefresh();
        return api(originalConfig);
      } catch (refreshErr) {
        if (refreshErr?.response?.status === 401) {
          endSession();
        }
        return Promise.reject(refreshErr);
      }
    }

    return Promise.reject(error);
  },
);
